/**
 * How the Android app is allowed to run on this device, for this person.
 *
 * Two answers, both decided by the server (GET /sync/devices/self) from the
 * property's setting and the person's role:
 *
 *  - `terminal`: the device is registered here, or registration is required and
 *    the setup screen is in the way. It keeps its own database and works offline.
 *  - `online`: nobody has registered it and nobody needs to. It runs like the
 *    website - every call goes to the server - and keeps no offline copy of the
 *    till. That copy is what registration exists to control (lock, wipe, track),
 *    so a device outside that control does not get one.
 *
 * Remembered per person, because the data mode is chosen at boot and the
 * server's answer needs the network. A registered tablet that starts up with no
 * signal must still come up in its offline mode, so the last answer is kept; and
 * it is the *last answer for this person*, because the owner and a cashier on the
 * same tablet can be told different things.
 */
export type DeviceAccess = 'terminal' | 'online';

const KEY = 'kortex.deviceAccess';

interface Stored {
  userId: string;
  access: DeviceAccess;
}

/**
 * Whether registration is required of this person.
 *
 * Only an explicit `false` means no. A server that predates the setting does not
 * send the field at all, and an app talking to one (a new APK installed before the
 * server was upgraded) must carry on as it always did - registration required -
 * rather than read a missing answer as permission to skip it.
 */
export const registrationRequired = (status: { required?: boolean }): boolean => status.required !== false;

/** What the server said, as the access it means. */
export const accessFromStatus = (status: { registered: boolean; required?: boolean }): DeviceAccess =>
  !status.registered && !registrationRequired(status) ? 'online' : 'terminal';

export function cachedDeviceAccess(userId: string | null | undefined): DeviceAccess | null {
  if (!userId) return null;
  try {
    const stored = JSON.parse(window.localStorage.getItem(KEY) ?? 'null') as Stored | null;
    return stored?.userId === userId && (stored.access === 'terminal' || stored.access === 'online')
      ? stored.access
      : null;
  } catch {
    return null;
  }
}

export function rememberDeviceAccess(userId: string, access: DeviceAccess): void {
  try {
    const current = window.localStorage.getItem(KEY);
    const next = JSON.stringify({ userId, access } satisfies Stored);
    if (current !== next) window.localStorage.setItem(KEY, next);
  } catch {
    // Storage unavailable: the answer is asked for again on the next start.
  }
}
